import { useState } from 'react';
import { Group, Badge, ActionIcon, Tooltip, ScrollArea } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconX, IconPin, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import type { KianEntityContext } from '../../../types/kian';
import { entityKey } from '../../../types/kian';
import './KianContextStack.module.css';

export interface KianContextStackProps {
  current: KianEntityContext | null;
  recent: KianEntityContext[];
  pinned: KianEntityContext[];
  pinEntity: (ctx: KianEntityContext) => void;
  unpinEntity: (key: string) => void;
  removeRecent: (key: string) => void;
}

function Chip({
  label,
  color,
  pulse,
  onRemove,
  onPin,
  showPin,
  pinnedHere,
}: {
  label: string;
  color: string;
  pulse?: boolean;
  onRemove?: () => void;
  onPin?: () => void;
  showPin?: boolean;
  pinnedHere?: boolean;
}) {
  return (
    <Group
      gap={2}
      wrap="nowrap"
      style={{
        flexShrink: 0,
        animation: pulse ? 'kianCtxPulse 0.6s ease-out 1' : undefined,
      }}
    >
      <Badge size="sm" variant="light" color={color}>
        {label}
      </Badge>
      <Group gap={0} wrap="nowrap">
        {showPin && onPin ? (
          <Tooltip label={pinnedHere ? 'Bereits angeheftet' : 'Kontext anheften'} withArrow>
            <ActionIcon
              size="xs"
              variant="subtle"
              color={pinnedHere ? 'cyberOrange' : 'gray'}
              onClick={() => onPin()}
              aria-label="Anheften"
            >
              <IconPin size={12} />
            </ActionIcon>
          </Tooltip>
        ) : null}
        {onRemove ? (
          <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => onRemove()} aria-label="Entfernen">
            <IconX size={12} />
          </ActionIcon>
        ) : null}
      </Group>
    </Group>
  );
}

/**
 * Kontext-Chips: aktuell (cyberOrange + pulse), angeheftet (cyberOrange + 📌), zuletzt (grau).
 * Auf Mobile: max. 2 Chips sichtbar, Rest hinter "+N"-Badge.
 */
export function KianContextStack({
  current,
  recent,
  pinned,
  pinEntity,
  unpinEntity,
  removeRecent,
}: KianContextStackProps) {
  const [expanded, setExpanded] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const pinnedKeys = new Set(pinned.map((p) => entityKey(p)));
  const recentTrim = recent
    .filter((r) => (!current || entityKey(r) !== entityKey(current)) && !pinnedKeys.has(entityKey(r)))
    .slice(0, 3);

  // Build flat list of all chip elements (preserves order: current → pinned → recent)
  const allChips: React.ReactNode[] = [];

  if (current) {
    allChips.push(
      <Chip
        key="__current__"
        label={current.label.slice(0, 48)}
        color="cyberOrange"
        pulse
        showPin
        pinnedHere={pinnedKeys.has(entityKey(current))}
        onPin={() => pinEntity({ ...current, visitedAt: Date.now() })}
        onRemove={undefined}
      />
    );
  } else {
    allChips.push(
      <Badge key="__free__" size="sm" variant="dot" color="gray">
        Freies Gespräch
      </Badge>
    );
  }

  for (const p of pinned) {
    allChips.push(
      <Chip
        key={entityKey(p)}
        label={`📌 ${p.label.slice(0, 40)}`}
        color="cyberOrange"
        onRemove={() => unpinEntity(entityKey(p))}
      />
    );
  }

  for (const r of recentTrim) {
    allChips.push(
      <Chip
        key={entityKey(r)}
        label={`↩ ${r.label.slice(0, 36)}`}
        color="gray"
        onRemove={() => removeRecent(entityKey(r))}
      />
    );
  }

  const MOBILE_VISIBLE = 2;
  const shouldCollapse = isMobile && allChips.length > MOBILE_VISIBLE;
  const visibleChips = shouldCollapse && !expanded ? allChips.slice(0, MOBILE_VISIBLE) : allChips;
  const hiddenCount = allChips.length - MOBILE_VISIBLE;

  return (
    <>
      <ScrollArea.Autosize mah={44} type="never" offsetScrollbars>
        <Group gap={6} wrap="nowrap" px="xs" py={6} style={{ borderBottom: '1px solid var(--mantine-color-dark-6)' }}>
          {visibleChips}

          {shouldCollapse && !expanded && (
            <Tooltip label="Alle Kontexte anzeigen" withArrow>
              <Badge
                size="sm"
                variant="outline"
                color="gray"
                style={{ cursor: 'pointer', flexShrink: 0 }}
                onClick={() => setExpanded(true)}
                rightSection={<IconChevronDown size={10} />}
              >
                +{hiddenCount}
              </Badge>
            </Tooltip>
          )}

          {shouldCollapse && expanded && (
            <Tooltip label="Einklappen" withArrow>
              <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setExpanded(false)}>
                <IconChevronUp size={12} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </ScrollArea.Autosize>
    </>
  );
}
